"use client";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, Package, ShoppingCart, Users, Tag, Palette,
  Ruler, FlaskConical, Truck, HelpCircle, Ticket, Hash,
  ArrowRight, Command,
} from "lucide-react";
import { useProducts }         from "./context/ProductsContext";
import { useOrders }           from "./context/OrdersContext";
import { useCustomers }        from "./context/CustomersContext";
import { useCategories }       from "./context/CategoryContext";
import { useColors }           from "./context/ColorContext";
import { useSizes }            from "./context/SizesContext";
import { useProductMaterials } from "./context/ProductMaterialContext";
import { useSuppliers }        from "./context/SuppliersContext";

// ─── Section config ──────────────────────────────────────────────────────────
const SECTIONS = [
  { key:"products",   label:"Products",   icon:Package,       color:"#0F766E", bg:"#F0FDFA" },
  { key:"orders",     label:"Orders",     icon:ShoppingCart,  color:"#3B82F6", bg:"#EFF6FF" },
  { key:"customers",  label:"Customers",  icon:Users,         color:"#7C3AED", bg:"#F5F3FF" },
  { key:"categories", label:"Categories", icon:Tag,           color:"#F59E0B", bg:"#FFFBEB" },
  { key:"colors",     label:"Colours",    icon:Palette,       color:"#EC4899", bg:"#FDF2F8" },
  { key:"sizes",      label:"Sizes",      icon:Ruler,         color:"#10B981", bg:"#F0FDF4" },
  { key:"materials",  label:"Materials",  icon:FlaskConical,  color:"#6366F1", bg:"#EEF2FF" },
  { key:"suppliers",  label:"Suppliers",  icon:Truck,         color:"#EA580C", bg:"#FFF7ED" },
];

// ─── Quick nav links ─────────────────────────────────────────────────────────
const QUICK_LINKS = [
  { label:"Dashboard",       path:"/dashboard",                      icon:Hash },
  { label:"Add Product",     path:"/products/add",                   icon:Package },
  { label:"View Orders",     path:"/orders",                         icon:ShoppingCart },
  { label:"Customers",       path:"/customers",                      icon:Users },
  { label:"Categories",      path:"/categories",                     icon:Tag },
  { label:"Settings",        path:"/settings",                       icon:Command },
  // ─── ADD THESE ───
  { label:"Colours",         path:"/product-attributes/colours",     icon:Palette },
  { label:"Sizes",           path:"/product-attributes/sizes",       icon:Ruler },
  { label:"Materials",       path:"/product-attributes/materials",   icon:FlaskConical },
];

// ─── Component ───────────────────────────────────────────────────────────────
export default function GlobalSearch({ open, onClose }) {
  const navigate   = useNavigate();
  const inputRef   = useRef(null);
  const listRef    = useRef(null);

  const [query,     setQuery]     = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  // Context data
  const { products = [] }          = useProducts()         || {};
  const { orders = [] }            = useOrders()           || {};
  const { customers = [] }         = useCustomers()        || {};
  const { categories = [] }        = useCategories()       || {};
  const { colors = [] }            = useColors()           || {};
  const { sizes = [] }             = useSizes()            || {};
  const { productMaterials = [] }  = useProductMaterials() || {};
  const { suppliers = [] }         = useSuppliers()        || {};

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Build result groups
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const groups = [];

    const match = (str) => (str || "").toLowerCase().includes(q);

    // Products
    const prods = products.filter(p =>
      match(p.name) || match(p.sku) || match(p.brand_name) || match(p.category_name)
    ).slice(0, 5).map(p => ({
      id:       `prod-${p.id}`,
      label:    p.name,
      sub:      [p.sku && `SKU: ${p.sku}`, p.category_name].filter(Boolean).join(" · "),
      section:  "products",
      path:     `/products`,
      badge:    p.status,
    }));
    if (prods.length) groups.push({ key:"products", items:prods });

    // Orders
    const ords = orders.filter(o =>
      match(o.orderNumber) || match(o.customer?.name) || match(o.customer?.email)
    ).slice(0, 5).map(o => ({
      id:    `ord-${o.id}`,
      label: `Order ${o.orderNumber}`,
      sub:   [o.customer?.name, o.rawStatus].filter(Boolean).join(" · "),
      section:"orders",
      path:  `/orders`,
      badge: o.rawStatus,
    }));
    if (ords.length) groups.push({ key:"orders", items:ords });

    // Customers
    const custs = customers.filter(c =>
      match(c.fullName) || match(c.email) || match(c.phone)
    ).slice(0, 5).map(c => ({
      id:    `cust-${c.id}`,
      label: c.fullName,
      sub:   [c.email, c.phone].filter(Boolean).join(" · "),
      section:"customers",
      path:  `/customers`,
    }));
    if (custs.length) groups.push({ key:"customers", items:custs });

    // Categories
    const cats = categories.filter(c => match(c.name) || match(c.description))
      .slice(0, 4).map(c => ({
        id:    `cat-${c.id}`,
        label: c.name,
        sub:   c.description || "",
        section:"categories",
        path:  `/categories`,
        badge: c.status,
      }));
    if (cats.length) groups.push({ key:"categories", items:cats });

    // Colors
    const cols = colors.filter(c => match(c.color_name) || match(c.code))
      .slice(0, 4).map(c => ({
        id:    `col-${c.id}`,
        label: c.color_name,
        sub:   c.code || "",
        section:"colors",
        path:  `/product-attributes/colours`,
        swatch: c.code,
      }));
    if (cols.length) groups.push({ key:"colors", items:cols });

    // Sizes
    const szs = sizes.filter(s => match(s.size || s.size_name))
      .slice(0, 4).map(s => ({
        id:    `sz-${s.id}`,
        label: s.size || s.size_name,
        sub:   "Size",
        section:"sizes",
        path:  `/product-attributes/Sizes`,
      }));
    if (szs.length) groups.push({ key:"sizes", items:szs });

    // Materials
    const mats = productMaterials.filter(m => match(m.name))
      .slice(0, 4).map(m => ({
        id:    `mat-${m.id}`,
        label: m.name,
        sub:   "Material",
        section:"materials",
        path:  `/product-attributes/materials`,
      }));
    if (mats.length) groups.push({ key:"materials", items:mats });

    // Suppliers
    const sups = suppliers.filter(s =>
      match(`${s.first_name} ${s.last_name}`) || match(s.phone) || match(s.email)
    ).slice(0, 4).map(s => ({
      id:    `sup-${s.id}`,
      label: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
      sub:   [s.phone, s.email].filter(Boolean).join(" · "),
      section:"suppliers",
      path:  `/suppliers`,
    }));
    if (sups.length) groups.push({ key:"suppliers", items:sups });

    return groups;
  }, [query, products, orders, customers, categories, colors, sizes, productMaterials, suppliers]);

  // Flat list for keyboard nav
  const flatItems = useMemo(() => results.flatMap(g => g.items), [results]);

  // Navigate on select
const selectItem = useCallback((item) => {
  navigate(item.path, { state: { prefillSearch: query.trim() } });
  onClose();
}, [navigate, onClose, query]);
  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flatItems.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
      if (e.key === "Enter" && flatItems[activeIdx]) { selectItem(flatItems[activeIdx]); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, flatItems, activeIdx, selectItem]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block:"nearest" });
  }, [activeIdx]);

  if (!open) return null;

  const totalResults = flatItems.length;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-start justify-center pt-[8vh] px-4"
      style={{ background:"rgba(0,0,0,0.45)", backdropFilter:"blur(4px)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
        style={{ maxHeight:"76vh" }}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
            placeholder="Search products, orders, customers, colours, sizes…"
            className="flex-1 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
          />
          {query && (
            <button onClick={() => setQuery("")} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs text-gray-400 border border-gray-200 rounded-lg">
            Esc
          </kbd>
        </div>

        {/* Results / Quick links */}
        <div ref={listRef} className="overflow-y-auto flex-1">
          {!query ? (
            /* Quick nav */
            <div className="p-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 pb-2">Quick Navigation</p>
              <div className="space-y-0.5">
                {QUICK_LINKS.map(link => {
                  const Icon = link.icon;
                  return (
                    <button key={link.path} onClick={() => { navigate(link.path); onClose(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left">
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <span className="font-medium">{link.label}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-300 ml-auto" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : results.length === 0 ? (
            /* No results */
            <div className="py-16 text-center">
              <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-400">No results for <span className="text-gray-600">"{query}"</span></p>
              <p className="text-xs text-gray-400 mt-1">Try searching by name, ID, email or phone</p>
            </div>
          ) : (
            /* Result groups */
            <div className="p-2 space-y-3">
              {(() => {
                let idx = 0;
                return results.map(group => {
                  const sec = SECTIONS.find(s => s.key === group.key);
                  const Icon = sec?.icon || Package;
                  return (
                    <div key={group.key}>
                      {/* Section header */}
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <div className="w-5 h-5 rounded-md flex items-center justify-center"
                          style={{ background: sec?.bg || "#F3F4F6" }}>
                          <Icon className="w-3 h-3" style={{ color: sec?.color || "#6B7280" }} />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest"
                          style={{ color: sec?.color || "#6B7280" }}>
                          {sec?.label || group.key}
                        </span>
                        <span className="text-[10px] text-gray-400 ml-1">({group.items.length})</span>
                      </div>

                      {/* Items */}
                      <div className="space-y-0.5">
                        {group.items.map(item => {
                          const itemIdx = idx++;
                          const isActive = activeIdx === itemIdx;
                          return (
                            <button
                              key={item.id}
                              data-idx={itemIdx}
                              onClick={() => selectItem(item)}
                              onMouseEnter={() => setActiveIdx(itemIdx)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left"
                              style={{ background: isActive ? (sec?.bg || "#F3F4F6") : "transparent" }}
                            >
                              {/* Color swatch or icon */}
                              {item.swatch ? (
                                <span className="w-6 h-6 rounded-full border border-black/10 flex-shrink-0"
                                  style={{ background: item.swatch }} />
                              ) : (
                                <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ background: sec?.bg || "#F3F4F6" }}>
                                  <Icon className="w-3 h-3" style={{ color: sec?.color || "#6B7280" }} />
                                </div>
                              )}

                              {/* Text */}
                              <div className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold text-gray-800 truncate">
                                  {highlight(item.label, query)}
                                </span>
                                {item.sub && (
                                  <span className="block text-xs text-gray-400 truncate mt-0.5">{item.sub}</span>
                                )}
                              </div>

                              {/* Badge */}
                              {item.badge && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                                  style={{
                                    background: /active|delivered|completed/i.test(item.badge) ? "#DCFCE7"
                                      : /cancel|inactive/i.test(item.badge) ? "#FEE2E2" : "#F3F4F6",
                                    color: /active|delivered|completed/i.test(item.badge) ? "#15803D"
                                      : /cancel|inactive/i.test(item.badge) ? "#991B1B" : "#6B7280",
                                  }}>
                                  {item.badge}
                                </span>
                              )}

                              {isActive && <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><kbd className="border border-gray-200 rounded px-1 bg-white">↑</kbd><kbd className="border border-gray-200 rounded px-1 bg-white">↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="border border-gray-200 rounded px-1 bg-white">Enter</kbd> open</span>
            <span className="flex items-center gap-1"><kbd className="border border-gray-200 rounded px-1 bg-white">Esc</kbd> close</span>
          </div>
          {query && totalResults > 0 && (
            <span className="text-[10px] text-gray-400 font-medium">{totalResults} result{totalResults !== 1 ? "s" : ""}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Highlight matching text
function highlight(text, query) {
  if (!query || !text) return text;
  const q = query.toLowerCase();
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background:"#FEF08A", color:"#713F12", borderRadius:2, padding:"0 1px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}
